import { Bell, AlertTriangle, Package, CheckCircle, Calendar, Clock, Loader2, BellOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useNotificationSchedules } from "@/hooks/useNotificationSchedules";
import { useActiveOrganization } from "@/hooks/useActiveOrganization";
import { formatCurrency } from "@/utils/currencyFormatter";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import PartialPaymentForm from "@/components/finance/PartialPaymentForm";
import { useTransactions } from "@/hooks/useTransactions";
import { useStockProducts } from "@/hooks/useStockProducts";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { format, isSameDay, parseISO, isAfter, isBefore, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";

interface NotificationBellProps {
  variant?: 'light' | 'dark';
}

export function NotificationBell({ variant = 'dark' }: NotificationBellProps) {
  const { activeOrganizationId } = useActiveOrganization();
  const { schedules, loading, markAsNotified, createScheduleForRemainingBalance, refreshSchedules } = useNotificationSchedules();
  const { addPartialPayment, getTransactionById } = useTransactions();
  const { getLowStockProducts, isLoading: stockLoading, refreshProducts } = useStockProducts();
  const [payingSchedule, setPayingSchedule] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("all");

  const today = startOfDay(new Date());

  // Improved filter: check if today matches start date period and notification day
  const todaySchedules = schedules.filter(schedule => {
    if (!schedule.isActive) return false;
    if (schedule.currentInstallment >= schedule.installmentsCount) return false;
    
    const startDate = parseISO(schedule.startDate);
    const endDate = schedule.endDate ? parseISO(schedule.endDate) : null;
    
    // Check if today is on or after start date
    const isAfterStart = !isBefore(today, startOfDay(startDate));
    // Check if today is on or before end date (if exists)
    const isBeforeEnd = !endDate || !isAfter(today, startOfDay(endDate));
    // Check if today's day matches the notification day
    const isCorrectDay = today.getDate() === schedule.notificationDay;
    
    return isCorrectDay && isAfterStart && isBeforeEnd;
  });

  // Get low stock products
  const lowStockProducts = getLowStockProducts();

  const chargesCount = todaySchedules.length;
  const stockCount = lowStockProducts.length;
  const totalNotifications = chargesCount + stockCount;
  const hasNotifications = totalNotifications > 0;

  // Calculate total outstanding amount for today's charges
  const totalOutstanding = todaySchedules.reduce((sum, s) => sum + s.installmentValue, 0);

  // Realtime subscription for schedules
  useEffect(() => {
    if (!activeOrganizationId) return;

    const channel = supabase
      .channel('notification-schedules-realtime')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'payment_notification_schedules',
        filter: `organization_id=eq.${activeOrganizationId}`
      }, () => {
        refreshSchedules();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeOrganizationId, refreshSchedules]);

  // Refetch data when popover opens
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      refreshSchedules();
      refreshProducts();
    }
  };

  const handleMarkNotified = (schedule: any) => {
    setPayingSchedule(schedule);
  };

  const handlePaymentSave = async (data: any) => {
    if (!payingSchedule) return;

    setProcessingId(payingSchedule.id);
    try {
      // Convert dd/MM/yyyy to YYYY-MM-DD
      const [day, month, year] = data.date.split('/');
      const formattedDate = `${year}-${month}-${day}`;

      // If schedule has linked transaction, use addPartialPayment
      if (payingSchedule.transactionId) {
        await addPartialPayment(
          payingSchedule.transactionId,
          data.value,
          formattedDate,
          data.paymentMethod
        );
      }

      // Mark as notified (advances installment counter)
      const result = await markAsNotified(payingSchedule);

      // Check remaining balance
      if (result.success && result.isComplete && payingSchedule.transactionId) {
        const updatedTransaction = await getTransactionById(payingSchedule.transactionId);
        
        if (updatedTransaction) {
          const originalAmount = updatedTransaction.original_amount || updatedTransaction.amount;
          const paidAmount = updatedTransaction.paid_amount || 0;
          const remainingBalance = originalAmount - paidAmount;

          if (remainingBalance > 0) {
            await createScheduleForRemainingBalance(payingSchedule, remainingBalance);
          }
        }
      }

      setPayingSchedule(null);
    } catch (error) {
      console.error('Error processing payment:', error);
    } finally {
      setProcessingId(null);
    }
  };

  if (!activeOrganizationId) return null;

  const renderEmptyState = (message: string) => (
    <div className="p-8 text-center">
      <BellOff className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );

  const renderChargeCard = (schedule: any) => {
    const currentInstallment = (schedule.currentInstallment || 0) + 1;
    const isProcessing = processingId === schedule.id;

    return (
      <div 
        key={schedule.id} 
        className="p-4 border rounded-lg bg-card hover:bg-accent/30 transition-colors space-y-3"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{schedule.patientName}</p>
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {schedule.description || 'Cobrança agendada'}
            </p>
          </div>
          <Badge variant="outline" className="shrink-0 text-xs">
            {currentInstallment}/{schedule.installmentsCount}
          </Badge>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold text-primary">
            {formatCurrency(schedule.installmentValue)}
          </span>
          
          <Button
            size="sm"
            variant="default"
            className="h-8 text-xs gap-1.5"
            onClick={() => handleMarkNotified(schedule)}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <CheckCircle className="h-3.5 w-3.5" />
            )}
            Marcar Pago
          </Button>
        </div>
      </div>
    );
  };

  const renderStockCard = (product: any) => {
    const isOutOfStock = product.current_quantity <= 0;
    
    return (
      <div 
        key={product.id} 
        className={cn(
          "p-4 border rounded-lg transition-colors",
          isOutOfStock 
            ? "bg-destructive/5 border-destructive/20" 
            : "bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800"
        )}
      >
        <div className="flex items-center gap-3">
          <Package className={cn(
            "h-5 w-5 shrink-0",
            isOutOfStock ? "text-destructive" : "text-amber-600"
          )} />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{product.name}</p>
            <p className={cn(
              "text-xs mt-0.5",
              isOutOfStock ? "text-destructive" : "text-amber-600 dark:text-amber-400"
            )}>
              {isOutOfStock 
                ? "Esgotado!" 
                : `${product.current_quantity} unid. (mín: ${product.minimum_quantity})`
              }
            </p>
          </div>
          {isOutOfStock && (
            <Badge variant="destructive" className="text-xs">Urgente</Badge>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className={cn(
              "relative",
              variant === 'light' 
                ? 'text-foreground hover:bg-muted' 
                : 'text-sidebar-foreground hover:bg-white/10'
            )}
          >
            <Bell className={cn("h-5 w-5", hasNotifications && "animate-pulse")} />
            {hasNotifications && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-medium">
                {totalNotifications > 9 ? '9+' : totalNotifications}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        
        <PopoverContent className="w-96 p-0" align="end">
          {/* Header */}
          <div className="p-4 border-b bg-muted/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-primary" />
                <h3 className="font-semibold">Notificações</h3>
              </div>
              <Badge variant={hasNotifications ? "destructive" : "secondary"} className="text-xs">
                {totalNotifications} pendente{totalNotifications !== 1 ? 's' : ''}
              </Badge>
            </div>
            {chargesCount > 0 && (
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {format(today, "dd 'de' MMMM", { locale: ptBR })}
              </p>
            )}
          </div>
          
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full grid grid-cols-3 h-10 rounded-none border-b bg-transparent p-0">
              <TabsTrigger 
                value="all" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
              >
                Todas
              </TabsTrigger>
              <TabsTrigger 
                value="charges" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent gap-1"
              >
                Cobranças
                {chargesCount > 0 && (
                  <span className="text-xs bg-primary/10 text-primary px-1.5 rounded-full">
                    {chargesCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="stock" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent gap-1"
              >
                Estoque
                {stockCount > 0 && (
                  <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400 px-1.5 rounded-full">
                    {stockCount}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="h-80">
              {loading || stockLoading ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mt-2">Carregando...</p>
                </div>
              ) : (
                <>
                  <TabsContent value="all" className="m-0 p-3 space-y-3">
                    {!hasNotifications ? (
                      renderEmptyState("Nenhuma notificação pendente")
                    ) : (
                      <>
                        {todaySchedules.map(renderChargeCard)}
                        {lowStockProducts.slice(0, 5).map(renderStockCard)}
                      </>
                    )}
                  </TabsContent>

                  <TabsContent value="charges" className="m-0 p-3 space-y-3">
                    {chargesCount === 0 ? (
                      renderEmptyState("Nenhuma cobrança para hoje")
                    ) : (
                      <>
                        {todaySchedules.map(renderChargeCard)}
                        
                        {/* Total Summary */}
                        <div className="p-3 bg-primary/5 rounded-lg border border-primary/20 mt-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Total a receber hoje</span>
                            <span className="text-lg font-bold text-primary">
                              {formatCurrency(totalOutstanding)}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </TabsContent>

                  <TabsContent value="stock" className="m-0 p-3 space-y-3">
                    {stockCount === 0 ? (
                      renderEmptyState("Estoque em dia!")
                    ) : (
                      <>
                        {lowStockProducts.map(renderStockCard)}
                        
                        {lowStockProducts.length > 5 && (
                          <Link to="/stock" onClick={() => setOpen(false)}>
                            <Button variant="ghost" size="sm" className="w-full text-xs">
                              Ver todos ({lowStockProducts.length} produtos)
                            </Button>
                          </Link>
                        )}
                      </>
                    )}
                  </TabsContent>
                </>
              )}
            </ScrollArea>
          </Tabs>
          
          {/* Footer */}
          <div className="p-3 border-t bg-muted/20 flex gap-2">
            <Link to="/finance" className="flex-1" onClick={() => setOpen(false)}>
              <Button variant="outline" size="sm" className="w-full text-xs">
                Ver Finanças
              </Button>
            </Link>
            <Link to="/stock" className="flex-1" onClick={() => setOpen(false)}>
              <Button variant="outline" size="sm" className="w-full text-xs">
                Ver Estoque
              </Button>
            </Link>
          </div>
        </PopoverContent>
      </Popover>

      {payingSchedule && (
        <PartialPaymentForm
          open={!!payingSchedule}
          onOpenChange={(open) => !open && setPayingSchedule(null)}
          transaction={{
            id: payingSchedule.transactionId || '',
            description: payingSchedule.description || payingSchedule.patientName,
            person: payingSchedule.patientName,
            valorOriginal: payingSchedule.totalAmount,
            valorPago: payingSchedule.installmentValue * (payingSchedule.currentInstallment || 0)
          }}
          suggestedValue={payingSchedule.installmentValue}
          onSave={handlePaymentSave}
        />
      )}
    </>
  );
}
